export type ButtonTypes = 'primary' | 'secondary' | 'cancel' | 'tertiary';

import { downscale } from 'src/conf/scale';
import { width, height, padding } from './general';

const navButtonSize = 120;
const navButtonMargin = 20;

const rectButtonPadding = 35;

const petsButtonSize = 145;

const buttonLabelOffset = { x: 0, y: -1 };
const drowpdownLabelOffset = { x: 0, y: -1 };

const buttons = {
  // ===========================================================================
  // Labels

  buttonLabelOffset,
  drowpdownLabelOffset,
  // ===========================================================================
  // Navigation Buttons

  navButtonSize,
  navButtonMargin,

  navMap: {
    width: navButtonSize,
    height: navButtonSize,
    x: width - navButtonSize / 2 - navButtonMargin,
    y: height - navButtonSize / 2 - navButtonMargin,
    centerOnOrigin: true,
    centerAnchor: true,
    image: 'assets/ui/shared/buttons/btn_nav_map.png',
  },

  navSlotsUp: {
    width: navButtonSize,
    height: navButtonSize,
    x: width - navButtonSize / 2 - navButtonMargin,
    y: height - navButtonSize / 2 - navButtonMargin,
    centerOnOrigin: true,
    centerAnchor: true,
    image: `assets/ui/shared/buttons/btn_nav_wheel_arrowup.png`,
  },

  navSlotsDown: {
    width: navButtonSize,
    height: navButtonSize,
    x: width - navButtonSize / 2 - navButtonMargin,
    y: height - navButtonSize / 2 - navButtonMargin,
    centerOnOrigin: true,
    centerAnchor: true,
    image: `assets/ui/shared/buttons/btn_nav_wheel_arrowdown.png`,
  },

  navSlotsLiteUp: {
    width: navButtonSize,
    height: navButtonSize,
    x: width - navButtonSize / 2 - navButtonMargin,
    y: height - navButtonSize / 2 - navButtonMargin,
    centerOnOrigin: true,
    centerAnchor: true,
    image: 'assets/ui/shared/buttons/btn_nav_slots_lite_top.png',
  },

  build: {
    width: navButtonSize,
    height: navButtonSize,
    x: navButtonSize / 2 + navButtonMargin,
    y: height - navButtonSize / 2 - navButtonMargin,
    centerOnOrigin: true,
    centerAnchor: true,
    image: 'assets/ui/shared/buttons/btn_nav_build.png',
  },

  cards: {
    width: navButtonSize,
    height: navButtonSize,
    x: navButtonSize / 2 + navButtonMargin,
    y: height - navButtonSize / 2 - navButtonMargin,
    centerOnOrigin: true,
    centerAnchor: true,
    image: 'assets/ui/shared/buttons/btn_nav_cards.png',
  },

  hideout: {
    width: navButtonSize,
    height: navButtonSize,
    x: navButtonSize / 2 + navButtonMargin,
    y: height - navButtonSize / 2 - navButtonMargin,
    centerOnOrigin: true,
    centerAnchor: true,
    image: 'assets/ui/shared/buttons/btn_hideout.png',
  },

  dailyBonus: {
    width: navButtonSize,
    height: navButtonSize,
    x: width - navButtonSize / 2 - navButtonMargin,
    y: height - navButtonSize / 2 - navButtonMargin,
    centerOnOrigin: true,
    centerAnchor: true,
  },

  pets: {
    width: petsButtonSize,
    height: petsButtonSize,
    x: petsButtonSize / 2 + navButtonMargin,
    y: height - petsButtonSize / 2 - 158, // value from Figma
    offsetFromBottom: 158, // value from Figma
    centerOnOrigin: true,
    centerAnchor: true,
  },

  // ===========================================================================
  // Close Buttons

  close: {
    zIndex: 1000,
    width: downscale(106),
    height: downscale(112),
    centerOnOrigin: true,
    centerAnchor: true,
    image: 'assets/ui/shared/buttons/btn_close.png',
    imagePressed: 'assets/ui/shared/buttons/btn_close_pressed.png',
    imageDisabled: 'assets/ui/shared/buttons/btn_close.png',
    pressedOffsetY: 5,
  },

  // ===========================================================================
  // Info Buttons

  info: {
    zIndex: 1000,
    width: downscale(106),
    height: downscale(112),
    centerOnOrigin: true,
    centerAnchor: true,
    image: 'assets/ui/shared/buttons/btn_info_v2.png',
    imagePressed: 'assets/ui/shared/buttons/btn_info_v2_pressed.png',
    imageDisabled: 'assets/ui/shared/buttons/btn_info_v2.png',
    pressedOffsetY: 5,
  },

  navNext: {
    image: 'assets/ui/tutorial/tutorial_arrow_active.png',
    imagePressed: 'assets/ui/tutorial/tutorial_arrow_active.png',
    imageDisabled: 'assets/ui/tutorial/tutorial_arrow_inactive.png',
    scaleMethod: 'contain' as const,
    pressedOffsetY: 5,
  },

  navPrevious: {
    image: 'assets/ui/tutorial/tutorial_arrow_active.png',
    imagePressed: 'assets/ui/tutorial/tutorial_arrow_active.png',
    imageDisabled: 'assets/ui/tutorial/tutorial_arrow_inactive.png',
    scaleMethod: 'contain' as const,
    pressedOffsetY: 5,
    flipX: true,
  },

  // Primary Action (Default - Used mainly for dialogs with a button to confirm)
  // Join - Collect - Claim - Play - Okay - Let’s Go! - Got It - Go! - Watch - Send
  // https://www.figma.com/file/C6SJSAEepxkdojEAgh4Dka/UI-Button-Template?node-id=0%3A1
  primary: {
    image: 'assets/ui/shared/buttons/btn_primary_up.png',
    imagePressed: 'assets/ui/shared/buttons/btn_primary_down.png',
    imageDisabled: 'assets/ui/shared/buttons/btn_primary_disabled.png',
    textColor: '#91808C',
    scaleMethod: '9slice' as const,
    sourceSlices: {
      horizontal: { left: rectButtonPadding, right: rectButtonPadding },
      vertical: { top: rectButtonPadding, bottom: rectButtonPadding },
    },
    imgScale: 0.66,
    pressedOffsetY: 5,
    disabledFontOffsetY: 5,
  },

  // Secondary action
  // Used as a CTA to get players to commit a certain action Friend Action
  // Invite - Brag - Attack - Revenge - Bribe - Poke - Choose FriendPurchase Action
  // (Used for In-game purchases, IAP purchases)- Buy - $ amount ($0.99, $4.99...) - Purchase
  // https://www.figma.com/file/C6SJSAEepxkdojEAgh4Dka/UI-Button-Template?node-id=0%3A1
  secondary: {
    image: 'assets/ui/shared/buttons/btn_secondary_up.png',
    imagePressed: 'assets/ui/shared/buttons/btn_secondary_down.png',
    imageDisabled: 'assets/ui/shared/buttons/btn_secondary_disabled.png',
    scaleMethod: '9slice' as const,
    sourceSlices: {
      horizontal: { left: rectButtonPadding, right: rectButtonPadding },
      vertical: { top: rectButtonPadding, bottom: rectButtonPadding },
    },
    imgScale: 0.66,
    pressedOffsetY: 5,
  },

  // Cancel Action (tappable but to deter players from tapping on it)
  // - Exit- Cancel - No Thanks
  // https://www.figma.com/file/C6SJSAEepxkdojEAgh4Dka/UI-Button-Template?node-id=0%3A1
  cancel: {
    image: 'assets/ui/shared/buttons/btn_cancel_up.png',
    imagePressed: 'assets/ui/shared/buttons/btn_cancel_down.png',
    imageDisabled: 'assets/ui/shared/buttons/btn_cancel_disabled.png',
    scaleMethod: '9slice' as const,
    sourceSlices: {
      horizontal: { left: rectButtonPadding, right: rectButtonPadding },
      vertical: { top: rectButtonPadding, bottom: rectButtonPadding },
    },
    imgScale: 0.66,
    pressedOffsetY: 5,
    disabledFontOffsetY: 5,
    labelOffsetY: 0,
  },

  pink: {
    image: 'assets/ui/shared/buttons/btn_pink_up.png',
    imagePressed: 'assets/ui/shared/buttons/btn_pink_down.png',
    imageDisabled: 'assets/ui/shared/buttons/btn_pink_up.png',
    scaleMethod: '9slice' as const,
    sourceSlices: {
      horizontal: { left: padding, right: padding },
      vertical: { top: padding, bottom: padding },
    },
    pressedOffsetY: 5,
  },

  blue: {
    image: 'assets/ui/shared/buttons/btn_blue_up.png',
    imagePressed: 'assets/ui/shared/buttons/btn_blue_down.png',
    imageDisabled: 'assets/ui/shared/buttons/btn_blue_up.png',
    scaleMethod: '9slice' as const,
    sourceSlices: {
      horizontal: { left: rectButtonPadding, right: rectButtonPadding },
      vertical: { top: rectButtonPadding, bottom: rectButtonPadding },
    },
    imgScale: 0.66,
    pressedOffsetY: 5,
  },

  dailyBonusWheel: {
    centerOnOrigin: true,
    x: width / 2,
    y: 840,
    width: 220,
    height: 95,
    align: 'center' as const,
    verticalAlign: 'center' as const,
    fontSize: 40,
    labelOffsetY: -2,
  },

  tab: {
    image: 'assets/ui/shared/buttons/tab_inactive.png',
    imagePressed: 'assets/ui/shared/buttons/tab_active.png',
    imageDisabled: 'assets/ui/shared/buttons/tab_active.png',
    imgScale: 0.66,
    scaleMethod: '9slice' as const,
    textColor: '#7192AB',
    disabledTextColor: '#9A7171',
    sourceSlices: {
      horizontal: { left: 32, right: 32 },
      vertical: { top: 5, bottom: 5 },
    },
    pressedOffsetY: 5,
  },

  mapButtonAd: {
    image: 'assets/ui/shared/buttons/btn_watch_ad_coins.png',
    width: 100,
    height: 100,
  },

  spinButtonAd: {
    image: 'assets/ui/shared/buttons/btn_watch_ad_spins.png',
    width: 100,
    height: 100,
  },

  spinButtonAdText: {
    x: 20 + 40,
    y: 58 + 10,
    width: 50,
    height: 20,
    centerOnOrigin: true,
    size: 30,
    align: 'center' as const,
    verticalAlign: 'center' as const,
  },

  consentInfoIcon: {
    width: 54,
    height: 54,
    x: 420,
    y: 645,
    image: 'assets/events/championship/btn_info.png',
    centerOnOrigin: true,
  },
  LINEButton: {
    image: 'assets/ui/demo/LINE_button.png',
    imagePressed: 'assets/ui/demo/LINE_button_down.png',
    imageDisabled: 'assets/ui/demo/LINE_button_down.png',
    scaleMethod: '3slice',
    sourceSlices: {
      horizontal: { left: 125, center: 20, right: 30 },
    },
    pressedOffsetY: 5,
    width: 424,
    height: 86,
    fontSize: 50,
    font: 'Body',
    textColor: '#FFFFFF',
    labelOffsetY: 1,
    labelPaddingX: 91,
    labelWidth: 450,
  } as const,
};

export type SkinButtonType = typeof buttons;
export default buttons;
